import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { HERO_IMAGE, REEL_URL } from "../lib/films";
import { Play, ArrowDown } from "lucide-react";
import { VideoModal } from "./VideoModal";

function scrollToId(id) {
  const el = document.getElementById(id);
  const lenis = window.__lenis;
  if (lenis && el) lenis.scrollTo(el, { offset: -20 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
}

export const Hero = () => {
  const ref = useRef(null);
  const [reelOpen, setReelOpen] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.18]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-bg grain"
    >
      {/* Parallax background image */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ y: bgY, scale: bgScale, opacity: bgOpacity }}
      >
        <img
          src={HERO_IMAGE}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "brightness(0.5) contrast(1.15) saturate(0.85)" }}
          loading="eager"
        />
      </motion.div>

      {/* Scrim */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-transparent via-bg/40 to-bg pointer-events-none" />
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-bg/70 via-transparent to-bg/40 pointer-events-none" />

      {/* Sun flare tucked top-right */}
      <div
        className="absolute z-0 pointer-events-none"
        style={{
          top: "18%",
          right: "8%",
          width: 380,
          height: 380,
          borderRadius: "9999px",
          background:
            "radial-gradient(circle, rgba(255,206,69,0.30), rgba(255,92,42,0.10) 40%, transparent 70%)",
          filter: "blur(6px)",
        }}
      />

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-32 md:pt-48 pb-14 min-h-screen flex flex-col justify-between"
      >
        {/* Top labels */}
        <div className="flex items-start justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            data-testid="hero-overline"
            className="font-mono text-[11px] tracking-widest2 uppercase text-muted flex items-center gap-3"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sun" />
            (01) — Writer · Director
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="hidden md:block font-mono text-[11px] tracking-widest2 uppercase text-muted text-right leading-relaxed"
          >
            <div>Los Angeles / Everywhere</div>
            <div className="mt-1 text-sun">◉ Portfolio · MMXXVI</div>
          </motion.div>
        </div>

        {/* Name — line-by-line masked reveal */}
        <div>
          <h1
            data-testid="hero-name"
            aria-label="Jy Tristian Wright"
            className="font-display leading-[0.86] tracking-tight text-[22vw] md:text-[15vw] lg:text-[12.5vw] text-ink"
          >
            <span className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}
                className="block"
              >
                Jy Tristian
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.em
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.82 }}
                className="block italic text-sun"
              >
                Wright.
              </motion.em>
            </span>
          </h1>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-end">
            <motion.p
              data-testid="hero-tagline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 1.15 }}
              className="md:col-span-7 font-display italic text-2xl md:text-3xl lg:text-4xl text-ink/85 leading-tight max-w-2xl"
            >
              Stories that transforms the soul and transcends lives.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 1.25 }}
              className="md:col-span-5 flex md:justify-end items-center"
            >
              <button
                data-testid="hero-reel-cta"
                onClick={() => setReelOpen(true)}
                className="group inline-flex items-center gap-4 pl-1.5 pr-6 py-1.5 rounded-full border border-ink/25 hover:border-sun hover:bg-sun/10 hover-fade"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-sun text-bg group-hover:scale-105 hover-fade">
                  <Play size={16} fill="currentColor" />
                </span>
                <span className="font-mono text-[11px] tracking-widest2 uppercase text-ink">
                  Play the Reel · 02'
                </span>
              </button>
            </motion.div>
          </div>
        </div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="flex items-center justify-between gap-4 font-mono text-[10px] tracking-widest2 uppercase text-muted"
        >
          <button
            data-testid="hero-scroll-cta"
            onClick={() => scrollToId("manifesto")}
            className="inline-flex items-center gap-3 hover:text-ink hover-fade"
          >
            <span>Scroll</span>
            <ArrowDown size={12} />
          </button>
          <div className="hidden md:block">© MMXXVI — Los Angeles</div>
          <div className="flex items-center gap-4">
            <span className="text-sun">◉ REC</span>
            <span>24 FPS</span>
            <span>35MM</span>
          </div>
        </motion.div>
      </motion.div>

      <VideoModal
        open={reelOpen}
        url={REEL_URL}
        title="Director's Reel — Jy Tristian Wright"
        onClose={() => setReelOpen(false)}
      />
    </section>
  );
};
